import React, { useState, useCallback } from 'react';
import axios from 'axios';
import ClearableInput from './ClearableInput';

const LocationAutocomplete = ({ value, onChange, onSelect, disabled, className, onClear }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const fetchSuggestions = useCallback(async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await axios.get('https://api.geoapify.com/v1/geocode/autocomplete', {
        params: {
          text: query,
          apiKey: '26c5ac382af44ebb9040d86d730657ea'
        }
      });

      const filteredSuggestions = response.data.features.map(feature => {
        const { city, state_code } = feature.properties;
        return city && state_code ? `${city}, ${state_code}` : feature.properties.formatted;
      });

      setSuggestions(filteredSuggestions);
      setHighlightedIndex(-1);
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
    }
  }, []);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    onChange(newValue);
    fetchSuggestions(newValue);
  }, [onChange, fetchSuggestions]);

  const selectSuggestion = useCallback((suggestion) => {
    onChange(suggestion);
    setSuggestions([]);
    onSelect(suggestion);
    setHighlightedIndex(-1);
  }, [onChange, onSelect]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0) {
        selectSuggestion(suggestions[highlightedIndex]);
      } else if (suggestions.length === 1) {
        selectSuggestion(suggestions[0]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
    }
  }, [highlightedIndex, suggestions, selectSuggestion]);

  return (
    <div className="location-autocomplete-wrapper">
      <ClearableInput
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder="Enter your city"
        disabled={disabled}
        className={`location-input ${className}`}
        name="location"
        onClear={onClear}
      />
      {suggestions.length > 0 && (
        <ul className="location-suggestions-list" role="listbox">
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              onClick={() => selectSuggestion(suggestion)}
              className={`location-suggestion-item ${highlightedIndex === index ? 'highlighted' : ''}`}
              role="option"
              aria-selected={highlightedIndex === index}
              onMouseEnter={() => setHighlightedIndex(index)}
              onMouseLeave={() => setHighlightedIndex(-1)}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LocationAutocomplete;